<template>
  <header :class="[$style['header'], newNotifications ? $style['header--with-notifications'] : '']">
    <div :class="$style['header--row']">
      <LogoInterfloraRound :class="$style['header__logo']" />
      <nav :class="$style['navbar']">
        <Dropdown
          v-if="isMultiShop"
          :label="'Mes boutiques'"
          :icon="'IconShop'"
          :is-mobile-nav="false"
          :class="[$style['dropdown']]"
        >
          <div
            v-for="shop in otherShops"
            :key="shop.FloristId"
            :class="$style['link--item']"
            @click="logAsNewFlorist(shop.FloristId)"
          >
            <Paragraph6 :class="$style['link--item--shop']">{{ shop.ShopLocation.Name }}</Paragraph6>
            <Paragraph5>{{ shop.FloristId }}</Paragraph5>
          </div>
        </Dropdown>
        <button
          v-if="ONBOARDING_GUIDES"
          :class="$style['link']"
          @click="showGuide('recipientConfirmation', 'manual')"
        >
          <UIcon
            name="material-symbols-light:developer-guide-outline"
            :class="$style['icon']"
          />
          <Paragraph4>{{ $t('onboarding.title') }}</Paragraph4>
        </button>
        <button
          v-if="!isDeliveryMan"
          :class="$style['link']"
          @click="displayPanel()"
        >
          <span
            v-if="newNotifications"
            :class="$style['pastille']"
          />
          <IconBell
            :size="20"
            fill="var(--color-primary-700)"
          />
          <Paragraph4>{{ $t('notification.title') }}</Paragraph4>
        </button>

        <Dropdown
          :label="internalUser ? floristId : floristInfo.floristId"
          :icon="'IconProfile'"
          :is-mobile-nav="false"
          :class="$style['dropdown']"
        >
          <NuxtLink
            v-if="isManager"
            :to="{ name: RouteNames.Admin }"
            :class="$style['link--item']"
          >
            <Paragraph4>{{ $t('nav.admin') }}</Paragraph4>
          </NuxtLink>
          <NuxtLink
            v-if="floristInfo"
            to="/"
            event=""
            :class="$style['link--item']"
            @click="logoutEvent"
          >
            <Paragraph4>{{ $t('nav.signOut') }}</Paragraph4>
          </NuxtLink>
        </Dropdown>
      </nav>
    </div>
    <div
      v-if="internalUser && localStorageNotEmpty && pathname !== RouteNames.InternalUserSelect"
      :class="$style['logAs']"
    >
      <Paragraph5>
        <Heading3>{{ $t('message.welcome', { name: floristId }) }}</Heading3>
        {{ $t('loginAs') }}
        <span>
          {{ floristInfo.shopLocation.name }}
          <span v-if="floristInfo.shopLocation.name !== floristInfo.floristId"> - {{ floristInfo.floristId }}</span>
        </span>
      </Paragraph5>
    </div>
  </header>
</template>

<script setup lang="ts">
import { Heading3, Paragraph4, Paragraph5, Paragraph6 } from '~/components-daisy/typography';
import { notificationParams } from '~/app/types/notificationType';
import { ref } from 'vue';
import { RouteNames } from '~/app/routes/routeNames';
import { type Shop } from '~/services/multi-shop';
import IconBell from '~/components-daisy/icons/icons/bell/IconBell.vue';
import LogoInterfloraRound from '~/components-daisy/logos/interflora/LogoInterfloraRound.vue';
import useOnboarding from '~/composables/onboarding/useOnboarding';

const floristInfoStore = useFloristInfoStore();
const internalUser = floristInfoStore.isInternalUser;
const floristId = floristInfoStore.adminLogin;
const floristInfo = ref(floristInfoStore.floristInfo);
const isManager = ref(floristInfoStore.floristInfo.users[0].role === 'SHOP_OWNER' ? true : false);
const isDeliveryMan = ref(floristInfoStore.isDeliveryMan);
const pathname = window.location.pathname.replace('/', '');
const localStorageNotEmpty = computed(() => {
  return localStorage.length > 0;
});

const logoutEvent = () => {
  const config = useRuntimeConfig();
  floristInfoStore.clearFloristInfo();
  logout(config.public.logoutUrl);
};

type Props = {
  newNotifications: boolean;
};

defineProps<Props>();

const { ONBOARDING_GUIDES } = await useCountryParams();
const { showGuide } = useOnboarding();

const emits = defineEmits(['notification']);

const displayPanel = () => {
  notificationParams.displayedPanel = !notificationParams.displayedPanel;
  return emits('notification', notificationParams.displayedPanel);
};

const isMultiShop = ref(false);
const shops = ref<Shop[]>([]);

const otherShops = computed(() => {
  return shops.value.filter((shop) => shop.FloristId !== floristInfo.value.floristId);
});

onMounted(async () => {
  if (localStorage.getItem('multiShop')) {
    shops.value = JSON.parse(localStorage.getItem('multiShop') ?? '');
    isMultiShop.value = shops.value.length > 1;
  }
});

const logAsNewFlorist = async (newFloristId: string) => {
  if (newFloristId === floristInfo.value.floristId) {
    return;
  }
  floristInfoStore.clearFloristInfo();
  localStorage.setItem('selectedFlorist', newFloristId);
  window.location.href = '/';
};
</script>

<style lang="postcss" module>
.header {
  align-items: flex-start;
  display: block;
  height: fit-content;
  justify-content: space-between;
  gap: 20px;
  transition: all 500ms;
  &--with-notifications {
    transition: all 500ms;
    height: 175px !important;
  }
  &--row {
    display: flex;
    justify-content: space-between;
    width: 100%;
  }
  @media screen and (max-width: 900px) {
    display: none;
  }
}

.navbar {
  align-items: center;
  display: flex;
  flex-direction: row;
  gap: var(--space-100);
  height: 75px;
  width: fit-content;
}

.link {
  align-items: center;
  background-color: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  gap: var(--space-100);
  height: 42px;
  justify-content: center;
  padding-left: 15px;
  padding-right: 5px;
  width: fit-content;
  min-width: 150px;
  &--item {
    align-items: center;
    cursor: pointer;
    display: flex;
    height: 42px;
    padding: var(--space-50) var(--space-100);
    width: 100%;
    z-index: 50;
    gap: 5px;

    &:hover {
      border-radius: var(--border-radius-100);
      background-color: var(--color-primary-200);
      color: var(--color-primary-700);
    }
    &--shop {
      max-width: 80%;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
  &:hover {
    background-color: var(--color-primary-200);
    border-radius: var(--border-radius-100);
    color: var(--color-primary-700);
  }
}

.dropdown {
  align-items: center;
  background-color: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  height: 42px;
  padding-inline: 5px;
  width: fit-content;

  &:hover {
    border-radius: var(--border-radius-100);
    background-color: var(--color-primary-200);
    color: var(--color-primary-700);
  }
  &--shop {
    width: 150px;
  }
}

.pastille {
  background-color: var(--color-primary-700);
  border-radius: var(--border-radius-600);
  box-shadow: var(--shadow-200);
  margin-right: -32px;
  margin-top: 15px;
  padding: 5px;
  position: relative;
}

.logAs {
  background-color: var(--color-primary-700);
  color: var(--color-mono-100);
  border: none;
  border-radius: var(--border-radius-200);
  display: flex;
  flex-direction: column;
  padding: 10px;
  gap: 20px;
  max-width: 600px;
  width: fit-content;
  min-width: 400px;
}

.icon {
  color: var(--color-primary-700);
  font-size: 20px;
}
</style>
